import type { TFunction } from 'i18next';
import type { ComponentType, ReactNode } from 'react';
import { useState } from 'react';
import {
  Bell,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Lightbulb,
  ListChecks,
  Moon,
  Rocket,
  Sparkles,
  TrendingUp,
  Trophy,
  Users,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { Department } from '@/api/types';
import { apiGet } from '@/api/client';
import { Alert } from '@/app/components/ui/Alert';
import { Badge } from '@/app/components/ui/Badge';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { Field, Select } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { Skeleton } from '@/app/components/ui/Skeleton';
import { StatTile, StatTileDeltaBadge } from '@/app/components/ui/StatTile';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { useMe } from '@/features/me/MeContext';
import { visibleDepartments } from '@/features/me/permissions';
import { listChallenges, type Challenge, type ChallengeCounts } from '@/features/challenges/api';
import {
  getParticipation,
  getParticipationSeries,
  type FunnelStep,
  type ParticipationRates,
  type ParticipationSeriesResult,
  type ParticipationTarget,
} from '@/features/participation/api';
import { fetchPilotParticipantCount, listPilots } from '@/features/pilots/api';
import { statusTone } from '@/features/pilots/statusTone';
import type { Pilot, PilotStatus } from '@/features/pilots/types';
import {
  runDashboardBatch,
  type DashboardPanelSpec,
  type DashboardRange,
} from '@/features/statistics/api';
import { RangeSelect } from '@/features/statistics/RangeSelect';
import { PanelOutcome, StatusNote } from '@/features/statistics/StatsPanel';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatDate, formatShortDate } from '@/lib/format';
import { useThemeColors } from '@/lib/useThemeColors';
import { deriveCards } from '@/features/vitality/deriveCards';
import {
  listLocations,
  vitalityPanels,
  type Location,
  type VitalityTarget,
} from '@/features/vitality/api';
import { buildInsightText, periodPhrase } from '@/features/vitality/insight';
import { deriveDailySeries, mostActiveWeekday, type DailyPoint } from '@/features/vitality/series';
import {
  buildActions,
  buildDevelopments,
  type ActionItem,
  type DevelopmentItem,
  type SummaryLine,
} from './summary';
import { useAsync } from '@/lib/useAsync';
import { cn } from '@/lib/cn';
import type { StatTileDelta } from '@/app/components/ui/StatTile';
import i18n from '@/i18n';

/** Resolves a `SummaryLine`'s dynamic key through `t()` — same bridge
 * `src/i18n/renderLabel.ts` uses for a data-carried key i18next's generated
 * types can't check against the catalogue's literal union at compile time. */
function tLine(t: TFunction, line: SummaryLine): string {
  return line.params ? t(line.key as never, line.params) : t(line.key as never);
}

/** One icon per `DevelopmentItem`/`ActionItem` id — doesn't need to be
 * fancy, just present; falls back to the card's own header icon when an id
 * isn't listed (shouldn't happen, but keeps a future trigger from rendering
 * with no icon at all). */
const DEVELOPMENT_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  stress: TrendingUp,
  sleep: Moon,
  vitalityGood: TrendingUp,
  needsReactivation: Users,
  mostActiveWeekday: TrendingUp,
  challengeParticipation: Users,
};

const ACTION_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  sendReminder: Bell,
  reactivate: Users,
  stressCheckIn: ClipboardCheck,
  sleepReminder: Moon,
  activeChallenge: ListChecks,
  pilotEvaluation: ClipboardCheck,
};

/** One "Belangrijkste ontwikkelingen" sub-card: a round icon, bold heading
 * and a description line below it, 2-per-row on wider screens. */
function DevelopmentTile({ item, t }: { item: DevelopmentItem; t: TFunction }) {
  const Icon = DEVELOPMENT_ICONS[item.id] ?? Lightbulb;
  return (
    <div className="border-line flex items-start gap-3 rounded-xl border p-3">
      <span
        aria-hidden="true"
        className="bg-mint/10 text-mint-deep flex size-8 shrink-0 items-center justify-center rounded-full"
      >
        <Icon className="size-4" />
      </span>
      <div className="flex flex-col gap-0.5">
        <p className="text-ink text-sm font-semibold">{tLine(t, item.title)}</p>
        <p className="text-accent text-sm">{tLine(t, item.body)}</p>
      </div>
    </div>
  );
}

/** One "Acties voor HR" row: icon + two-line text (bold title, smaller
 * subtitle naming the concrete next step) + a right-aligned chevron that's
 * a real link when the action has somewhere to go, plain text otherwise
 * (e.g. `stressCheckIn`/`sleepReminder`, which are just suggestions). */
function ActionRow({ item, t }: { item: ActionItem; t: TFunction }) {
  const Icon = ACTION_ICONS[item.id] ?? ListChecks;
  const content = (
    <>
      <span
        aria-hidden="true"
        className="bg-mint/10 text-mint-deep flex size-8 shrink-0 items-center justify-center rounded-full"
      >
        <Icon className="size-4" />
      </span>
      <span className="flex flex-1 flex-col gap-0.5">
        <span className="text-ink text-sm font-semibold">{tLine(t, item.title)}</span>
        <span className="text-accent text-xs">{tLine(t, item.subtitle)}</span>
      </span>
      {item.linkTo && <ChevronRight aria-hidden="true" className="text-accent size-4 shrink-0" />}
    </>
  );
  return (
    <li>
      {item.linkTo ? (
        <Link
          to={item.linkTo}
          className="hover:bg-mint/5 -m-2 flex items-center gap-3 rounded-xl p-2"
        >
          {content}
        </Link>
      ) : (
        <div className="flex items-center gap-3">{content}</div>
      )}
    </li>
  );
}

function withId(
  rows: readonly Department[],
): (Department & { id: string; company_id: string | null })[] {
  return rows.map((d) => ({ ...d, id: d.id ?? '', company_id: d.company_id ?? null }));
}

function funnelStep(
  funnel: readonly FunnelStep[] | undefined,
  key: FunnelStep['key'],
): FunnelStep | null {
  return funnel?.find((step) => step.key === key) ?? null;
}

function averageRate(rates: ParticipationRates | undefined): number | null {
  if (!rates) return null;
  const values = Object.values(rates).filter((v): v is number => v !== null);
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/** A tile's period-over-period delta from a real current/previous pair —
 * `null` when either side is missing, so `StatTile` never shows an invented
 * or approximate change (§ no delta for Betrokkenheid/Actieve deelnemers
 * below: the participation API has no previous-period figure for either,
 * only `pct_of_previous` per *funnel step*, a conversion rate, not a trend). */
function deltaFrom(
  current: number | null | undefined,
  previous: number | null | undefined,
  goodDirection: 'up' | 'down',
): StatTileDelta | null {
  if (current == null || previous == null || previous === 0) return null;
  return { pct: Math.round(((current - previous) / previous) * 100), goodDirection };
}

/**
 * `/overview` — Home's first subitem (§ Navigatiemodel, PLAN-FASE2.md
 * WP-06): the company-dashboard summary that builds on Vitality (WP-02),
 * Participation (WP-03), Pilots (WP-04) and Challenges (WP-05). Each card
 * below calls its own data source directly (Verify: "elke kaart z'n eigen
 * databron aanroept") — only `TrendsSection` batches several panels into one
 * `/statistics/dashboard` call, same one-call-for-several-panels idea
 * `StatsGrid` (Fase1 WP-09) uses, but laid out by hand (see that function's
 * own doc comment) so real metrics and "not available yet" placeholders can
 * sit in the exact grid position the redesign calls for.
 */
export function OverviewPage() {
  const { company } = useActiveCompany();
  const { t } = useTranslation();

  if (company === null) {
    return (
      <section className="page flex flex-col gap-6">
        <PageHeader title={t('overview.page.title')} />
        <p className="text-ink text-sm">{t('overview.page.noScope')}</p>
      </section>
    );
  }

  return <CompanyOverview companyId={company.id} companyName={company.name} />;
}

/** TEMP (§ testing with fresh/sparse demo data): shortened from
 * `last_30_days` to the API's shortest built-in preset so the summary card
 * and figures populate without a month of backfilled data. Revert to
 * `last_30_days` once there's enough seeded history — the insight
 * sentence's period wording (`periodPhrase`) follows this automatically,
 * nothing else to update. */
const OVERVIEW_RANGE: DashboardRange = 'last_7_days';

function CompanyOverview({ companyId, companyName }: { companyId: string; companyName: string }) {
  const { t } = useTranslation();
  const { permissions } = useMe();

  const departmentsState = useAsync(
    () => apiGet('/companies/{companyId}/departments', { params: { companyId } }),
    [companyId],
  );
  const departments = departmentsState.data
    ? visibleDepartments(permissions, companyId, withId(departmentsState.data)).filter((d) =>
        permissions.canViewDepartmentStats({ id: d.id, company_id: d.company_id }),
      )
    : [];
  const departmentId = departments[0]?.id ?? '';

  // § Openstaande punten: `/statistics/dashboard` has no company-wide
  // target (WP-02's own open point — "vereist het kiezen van één
  // afdeling"), so the vitality-derived numbers (Vitaliteit, Stress, Slaap)
  // are scoped to the first department this caller may see, same as
  // `VitalityPage` defaults. `/statistics/participation`, unlike the
  // dashboard batch endpoint, *does* support a company-wide target (WP-03),
  // so Betrokkenheid/Actieve deelnemers below are genuinely company-wide.
  const vitalityTarget = departmentId ? { department_id: departmentId } : null;
  const vitalityBatch = useAsync(
    () =>
      vitalityTarget
        ? runDashboardBatch(vitalityPanels(vitalityTarget, OVERVIEW_RANGE))
        : Promise.resolve(null),
    [departmentId],
  );
  const trend = vitalityBatch.data?.panels.trend;
  const restingCurrent = vitalityBatch.data?.panels['resting-current'];
  const restingPrevious = vitalityBatch.data?.panels['resting-previous'];
  const cards = deriveCards(trend, restingCurrent, restingPrevious);

  const participation = useAsync(
    () => getParticipation({ type: 'company', id: companyId }, OVERVIEW_RANGE),
    [companyId],
  );
  const funnel = participation.data?.status === 'ok' ? participation.data.funnel : undefined;
  const rates = participation.data?.status === 'ok' ? participation.data.rates : undefined;
  const engagementPct = averageRate(rates);

  // Distinguishes "gated by the min-group-size privacy floor" (a real,
  // explainable reason — same `insufficient_data` status the Trends section
  // below already surfaces via `StatusNote`) from a genuine lack of data, so
  // the summary card doesn't read as broken when it's working as intended.
  const insufficientData =
    cards?.vitalityScore.status === 'insufficient_data' ||
    participation.data?.status === 'insufficient_data';

  const dailySeries = deriveDailySeries(vitalityBatch.data?.panels.daily);
  const insightText = cards
    ? buildInsightText({
        cards,
        dailySeries,
        participationPct: funnelStep(funnel, 'active_30d')?.pct_of_previous ?? null,
        period: periodPhrase(OVERVIEW_RANGE, t),
        locale: i18n.language,
        t,
      })
    : null;

  const pilots = useAsync(() => listPilots(companyId), [companyId]);
  const challenges = useAsync(() => listChallenges(companyId), [companyId]);

  const invited = funnelStep(funnel, 'invited');

  // "Meeste energie op {{weekday}}" development — the weekday (in the
  // viewer's own locale) with the highest average step count, already
  // sitting unused in `dailySeries` (only fed into `insightText` above).
  const mostActiveWeekdayName = mostActiveWeekday(dailySeries?.activity ?? [], i18n.language);

  // "Challenge-deelname {{pct}}%" development — `invited.count` is the
  // closest existing proxy for total headcount (same one `sendReminder`
  // below already uses), so this stays `null` rather than a divide-by-zero
  // 0% when there's no headcount to divide by.
  const challengeParticipationPct =
    invited !== null && invited.count > 0 && challenges.data
      ? Math.round((challenges.data.counts.total_participants / invited.count) * 100)
      : null;

  // "'{{name}}' loopt — plan de volgende" action — the first active
  // challenge, if any; don't overthink multiple active challenges.
  const firstActiveChallenge =
    challenges.data?.challenges.find((c) => c.status === 'active') ?? null;
  const activeChallenge = firstActiveChallenge ? { title: firstActiveChallenge.title } : null;

  // "Pilot '{{name}}' is klaar voor … evaluatie" action — the first pilot
  // sitting in either review status, if any.
  const firstPilotForEvaluation =
    pilots.data?.find((p) => p.status === 'interim_review' || p.status === 'final_review') ?? null;
  const pilotNeedingEvaluation = firstPilotForEvaluation
    ? {
        id: firstPilotForEvaluation.id,
        name: firstPilotForEvaluation.name,
        phase: (firstPilotForEvaluation.status === 'interim_review' ? 'interim' : 'final') as
          'interim' | 'final',
      }
    : null;

  const summaryInputs = {
    vitalityScore: cards?.vitalityScore ?? null,
    stress: cards?.stress ?? null,
    sleep: cards?.sleep ?? null,
    activeParticipants: funnelStep(funnel, 'active_30d'),
    invited,
    activated: funnelStep(funnel, 'activated'),
    needsReactivation: funnelStep(funnel, 'needs_reactivation'),
    mostActiveWeekday: mostActiveWeekdayName,
    challengeParticipationPct,
    activeChallenge,
    pilotNeedingEvaluation,
  };
  const developments = buildDevelopments(summaryInputs);
  const actions = buildActions(summaryInputs);

  if (departmentsState.data && departments.length === 0) {
    return (
      <section className="page flex flex-col gap-6">
        <PageHeader title={t('overview.page.title')} />
        <Alert tone="info">{t('overview.page.noDepartments')}</Alert>
      </section>
    );
  }

  return (
    <section className="page">
      <PageHeader title={t('overview.page.title')} />

      {/* Half the gap between the lead text and the blocks below versus a
       * plain `gap-6` container: `PageHeader` already carries its own
       * `mb-6`, so this inner wrapper's `gap-6` only spaces the blocks that
       * follow it from each other, not from the header. */}
      <div className="flex flex-col gap-6">
        <Card>
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-mint/10 text-mint-deep flex size-9 shrink-0 items-center justify-center rounded-full"
            >
              <Sparkles className="size-5" />
            </span>
            <div className="flex flex-col gap-2">
              <h2 className="font-heading text-ink text-base font-semibold">
                {t('overview.page.lead', { company: companyName })}
              </h2>
              {vitalityBatch.loading || participation.loading ? (
                <Skeleton kind="lines" />
              ) : (
                <p className="text-ink text-sm">
                  {insufficientData
                    ? t('statistics.status.insufficientData')
                    : (insightText ?? t('vitality.insight.unavailable'))}
                </p>
              )}
              <p className="text-accent text-xs">{t('overview.summary.caption')}</p>
            </div>
          </div>
        </Card>

        <WhatYouSeeLegend />

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatTile
            label={t('overview.figures.vitality')}
            value={cards?.vitalityScore.value != null ? Math.round(cards.vitalityScore.value) : '—'}
            delta={deltaFrom(cards?.vitalityScore.value, cards?.vitalityScore.previous, 'up')}
            indicator={<ProvenanceDot kind="aiDerived" />}
            info={t('overview.figuresInfo.vitality')}
          />
          <StatTile
            label={t('overview.figures.stress')}
            value={cards?.stress.value != null ? Math.round(cards.stress.value) : '—'}
            delta={deltaFrom(cards?.stress.value, cards?.stress.previous, 'down')}
            indicator={<ProvenanceDot kind="measured" />}
            info={t('overview.figuresInfo.stress')}
          />
          <StatTile
            label={t('overview.figures.sleep')}
            value={cards?.sleep.value != null ? `${Math.round(cards.sleep.value / 3600)}h` : '—'}
            delta={deltaFrom(cards?.sleep.value, cards?.sleep.previous, 'up')}
            indicator={<ProvenanceDot kind="measured" />}
            info={t('overview.figuresInfo.sleep')}
          />
          {/* Betrokkenheid/Actieve deelnemers have no previous-period figure
           * anywhere in the participation API (see `deltaFrom`'s doc
           * comment) — no delta rather than an invented one. */}
          <StatTile
            label={t('overview.figures.engagement')}
            value={engagementPct != null ? `${Math.round(engagementPct)}%` : '—'}
            indicator={<ProvenanceDot kind="usage" />}
            info={t('overview.figuresInfo.engagement')}
          />
          <StatTile
            label={t('overview.figures.activeParticipants')}
            value={summaryInputs.activeParticipants?.count ?? '—'}
            indicator={<ProvenanceDot kind="usage" />}
            info={t('overview.figuresInfo.activeParticipants')}
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader title={t('overview.developments.title')} icon={Lightbulb} />
            {developments.length === 0 ? (
              <EmptyState text={t('overview.developments.empty')} />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {developments.map((item) => (
                  <DevelopmentTile key={item.id} item={item} t={t} />
                ))}
              </div>
            )}
          </Card>

          <Card>
            <CardHeader title={t('overview.actions.title')} icon={ListChecks} />
            {actions.length === 0 ? (
              <EmptyState text={t('overview.actions.empty')} />
            ) : (
              <ul className="flex flex-col gap-2">
                {actions.map((item) => (
                  <ActionRow key={item.id} item={item} t={t} />
                ))}
              </ul>
            )}
          </Card>
        </div>

        <EnergyWellbeingCard rates={rates} />

        <div className="grid gap-6 lg:grid-cols-2">
          <PilotStatusCard pilots={pilots.data} loading={pilots.loading} error={pilots.error} />
          <ActiveChallengesCard
            challenges={challenges.data?.challenges}
            counts={challenges.data?.counts}
            loading={challenges.loading}
            error={challenges.error}
          />
        </div>

        <TrendsSection companyId={companyId} departments={departments} />

        <p className="text-accent flex items-center gap-1.5 text-xs">
          <Sparkles className="size-3.5 shrink-0" aria-hidden="true" />
          {t('overview.page.disclaimer')}
        </p>
      </div>
    </section>
  );
}

/**
 * The three provenance levels a tile or chart on this page can carry —
 * mirrors `src/features/vitality/metrics.ts`'s own `MetricBadge`
 * ('measured'/'aiDerived') plus a third, 'usage', for the participation-based
 * figures (Betrokkenheid/Actieve deelnemers) that are neither a wearable
 * reading nor a computed composite. One color per level, shared by
 * `WhatYouSeeLegend` and each `StatTile`'s own dot below it, so the legend
 * stays a real legend rather than a one-off explanation.
 */
type Provenance = 'measured' | 'aiDerived' | 'usage';

const PROVENANCE_DOT_CLASS: Record<Provenance, string> = {
  measured: 'bg-mint',
  aiDerived: 'bg-warning',
  usage: 'bg-info',
};

function ProvenanceDot({ kind }: { kind: Provenance }) {
  return (
    <span
      aria-hidden="true"
      className={cn('mt-0.5 size-2 shrink-0 rounded-full', PROVENANCE_DOT_CLASS[kind])}
    />
  );
}

function WhatYouSeeLegend() {
  const { t } = useTranslation();
  return (
    <div className="border-line flex flex-col gap-2 rounded-2xl border p-4 text-xs sm:flex-row sm:items-start sm:gap-6">
      <p className="text-ink shrink-0 font-semibold tracking-wide uppercase">
        {t('overview.legend.title')}
      </p>
      <ul className="text-accent flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:gap-x-6 sm:gap-y-2">
        <li className="flex items-start gap-1.5">
          <ProvenanceDot kind="measured" />
          {t('overview.legend.measured')}
        </li>
        <li className="flex items-start gap-1.5">
          <ProvenanceDot kind="aiDerived" />
          {t('overview.legend.aiDerived')}
        </li>
        <li className="flex items-start gap-1.5">
          <ProvenanceDot kind="usage" />
          {t('overview.legend.usage')}
        </li>
      </ul>
    </div>
  );
}

/** One Trends card — a title plus its chart/placeholder body, same `Card`
 * shape either way so a real metric and a "not available yet" one sit
 * flush in the same grid. */
function TrendCard({
  title,
  info,
  children,
}: {
  title: string;
  info?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader title={title} {...(info !== undefined ? { info } : {})} />
      {children}
    </Card>
  );
}

/** One Trends chart plotting a `DailyPoint[]` — `deriveDailySeries`'s own
 * per-day `vitalityScore`/`recovery` composite output — as a full line
 * chart, matching `StatsPanel.tsx`'s `SeriesPanel` day-grain chart (not
 * `VitalityCard.tsx`'s compact `Sparkline`, a different, smaller treatment
 * for a different page) since these sit flush next to that same chart style
 * in this section. `DailyPoint[]` is already `{date, value}[]`, exactly
 * what `dataKey="date"`/`dataKey="value"` below expect — no reshaping.
 * Falls back to the shared "no data" alert when every point's value is
 * `null` (as opposed to `insufficient_data`, which the caller's own
 * `status` check handles before this ever renders) — an empty line chart
 * reads as broken, not "no data yet". */
function DailySeriesChart({ points, color }: { points: DailyPoint[]; color: string }) {
  const { t } = useTranslation();
  const colors = useThemeColors();

  if (points.every((point) => point.value === null)) {
    return (
      <Alert tone="info" role="status">
        {t('statistics.status.noData')}
      </Alert>
    );
  }

  return (
    <div className="h-64 w-full" data-testid="daily-series-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points}>
          <CartesianGrid stroke={`${colors.ink}1a`} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: colors.ink }}
            tickFormatter={(value: unknown) => formatShortDate(String(value))}
            minTickGap={24}
          />
          <YAxis tick={{ fontSize: 11, fill: colors.ink }} />
          <Tooltip
            contentStyle={{
              background: 'var(--paper)',
              border: '1px solid var(--ink)',
              borderRadius: 12,
            }}
            labelFormatter={(value: unknown) => formatDate(String(value))}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            dot={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/** A `DailySeriesTrendCard`'s header `action`: the same delta badge look
 * `StatTile`'s own period-over-period delta uses (`StatTileDeltaBadge`,
 * exported from `StatTile.tsx` for exactly this reuse) plus the "vs vorige
 * periode" caption next to it — a real i18n key rather than a hardcoded
 * "vorige week", since `OVERVIEW_RANGE` isn't necessarily a week (see
 * `periodPhrase` in `vitality/insight.ts` for how this page already talks
 * about "the period" generically). */
function TrendDeltaBadge({ delta }: { delta: StatTileDelta }) {
  const { t } = useTranslation();
  return (
    <span className="flex items-center gap-1 text-xs">
      <StatTileDeltaBadge delta={delta} />
      <span className="text-accent">{t('overview.trends.vsPreviousPeriod')}</span>
    </span>
  );
}

/** A Trends card built from the `daily` panel's own per-day composite
 * series (§ The gap this WP closes): Vitaliteitsscore-over-tijd and
 * Herstel-trend used to be hardcoded "not available yet" placeholders even
 * though the exact numbers they need (`deriveDailySeries`'s
 * `vitalityScore`/`recovery`, the same `compositeScore`/`normalize` math
 * `deriveCards.ts` uses for the range-aggregate cards) were already being
 * computed on this page — just never rendered as a chart, only fed into the
 * "Hoe gaat het met…" summary sentence. `status` is the `daily` panel's own
 * (`vitalityBatch.data?.panels.daily?.status`) rather than re-derived, so a
 * privacy-floored group (`insufficient_data`) or a 403 (`forbidden`) shows
 * the same `StatusNote` the other Trends cards use for that same panel,
 * instead of a chart built from empty data. */
function DailySeriesTrendCard({
  title,
  info,
  points,
  color,
  status,
  loading,
  delta,
}: {
  title: string;
  info?: string;
  points: DailyPoint[];
  color: string;
  status: 'ok' | 'forbidden' | 'insufficient_data' | undefined;
  loading: boolean;
  delta: StatTileDelta | null;
}) {
  return (
    <Card>
      <CardHeader
        title={title}
        {...(info !== undefined ? { info } : {})}
        {...(delta ? { action: <TrendDeltaBadge delta={delta} /> } : {})}
      />
      {loading ? (
        <Skeleton kind="card" />
      ) : status && status !== 'ok' ? (
        <StatusNote status={status} />
      ) : (
        <DailySeriesChart points={points} color={color} />
      )}
    </Card>
  );
}

/** "Actieve gebruikers per week": `GET /statistics/participation/series`
 * (§ `PLAN-participation-series.md` in the API repo) — a weekly bar chart,
 * separate from the `/statistics/dashboard` batch the other Trends cards
 * share, since participation isn't a wearable metric in that endpoint's
 * catalogue. The endpoint no longer gates on `min_group_size` (it always
 * returns `status: 'ok'` with a real, possibly empty, `series`), so an
 * `insufficient_data` result is no longer a real response this endpoint can
 * send — an empty series and a genuine `error` are the only two non-chart
 * states left worth telling apart. */
function ActiveUsersPerWeekCard({
  state,
}: {
  state: { data: ParticipationSeriesResult | null; loading: boolean; error: Error | null };
}) {
  const { t } = useTranslation();
  const colors = useThemeColors();

  return (
    <TrendCard
      title={t('overview.trends.activeUsersPerWeek')}
      info={t('overview.trends.info.activeUsersPerWeek')}
    >
      {state.loading ? (
        <Skeleton kind="card" />
      ) : state.error ? (
        <Alert tone="danger">
          {t('statistics.status.loadFailed', { message: state.error.message })}
        </Alert>
      ) : !state.data || state.data.status !== 'ok' || state.data.series.length === 0 ? (
        <Alert tone="info" role="status">
          {t('statistics.status.noData')}
        </Alert>
      ) : (
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={state.data.series}>
              <CartesianGrid stroke={`${colors.ink}1a`} />
              <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: colors.ink }} />
              <YAxis tick={{ fontSize: 11, fill: colors.ink }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  background: 'var(--paper)',
                  border: '1px solid var(--ink)',
                  borderRadius: 12,
                }}
              />
              <Bar dataKey="active_count" fill={colors.mintDeep} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </TrendCard>
  );
}

/** "Alle deelnemers"/"Leidinggevenden"/"Medewerkers" — `undefined` (all)
 * sends no `role` at all; the other two map straight onto the API's own
 * `VitalityTarget.role`/participation-series `role` values. */
type ParticipantFilter = 'all' | 'manager' | 'employee';

const ALL_LOCATIONS = 'all';

/** The same explicit `{from, to}` `RangeSelect`'s own "Laatste 90 dagen"
 * option computes (kept as a private one-liner here rather than exported
 * from `RangeSelect.tsx` — a plain function export there trips
 * `react-refresh/only-export-components`, since that file otherwise only
 * exports the `RangeSelect` component). Used only to seed the Trends
 * section's default period — `RangeSelect` recomputes the same value itself
 * once the dropdown's "Laatste 90 dagen" option is picked. */
function ninetyDaysRange(): { from: string; to: string } {
  const to = new Date().toISOString().slice(0, 10);
  const from = new Date(Date.now() - 89 * 86_400_000).toISOString().slice(0, 10);
  return { from, to };
}

/** The 4 day-grain wearable-trend panels `CompanyOverview` used to build
 * directly before this WP — one `series` panel per metric, now
 * parameterized by the Trends section's own filtered target/range instead
 * of the page-wide `OVERVIEW_RANGE`/first-visible-department target the top
 * figures still use (see `CompanyOverview`'s own `vitalityTarget` doc
 * comment for why that scope stays as-is). */
function trendSeriesPanels(target: VitalityTarget, range: DashboardRange): DashboardPanelSpec[] {
  return [
    {
      id: 'sleep-trend',
      type: 'series',
      target,
      grain: 'day',
      range,
      metrics: ['sleep_duration'],
    },
    { id: 'activity-trend', type: 'series', target, grain: 'day', range, metrics: ['steps'] },
    { id: 'stress-trend', type: 'series', target, grain: 'day', range, metrics: ['stress'] },
    { id: 'hrv-trend', type: 'series', target, grain: 'day', range, metrics: ['hrv'] },
  ];
}

/**
 * The Trends section (§ Hoofdmenu redesign): its own filter row (period,
 * locatie, afdeling, deelnemers — the controls that used to be a "coming
 * later" placeholder) drives every chart below it, so this section owns its
 * own state and its own fetches rather than reading `panels`/`cards`
 * computed upstream in `CompanyOverview` for a *different*, fixed scope
 * (the top figures/summary sentence). Defaults: "Laatste 90 dagen" (the
 * design's own default period, `ninetyDaysRange()` — same explicit
 * `{from,to}` `RangeSelect` itself computes for that option), "Alle
 * locaties", "Alle afdelingen" (company-wide, via the new `company_id`
 * target kind), "Alle deelnemers".
 *
 * One `runDashboardBatch` call combines `vitalityPanels` (trend/
 * resting-current/resting-previous/daily) with the 4 day-grain wearable
 * panels above into a single POST,
 * same one-call-for-several-panels shape
 * the previous, upstream-fetched version used. `ActiveUsersPerWeekCard`
 * keeps its own separate fetch (participation isn't part of the
 * dashboard-batch endpoint's panel catalogue) but now reads the same filter
 * state for its target/`locationId`/`role`.
 */
function TrendsSection({
  companyId,
  departments,
}: {
  companyId: string;
  departments: readonly (Department & { id: string; company_id: string | null })[];
}) {
  const { t } = useTranslation();
  const colors = useThemeColors();

  const [departmentId, setDepartmentId] = useState(''); // '' = "Alle afdelingen" (company-wide)
  const [locationId, setLocationId] = useState(ALL_LOCATIONS);
  const [participantFilter, setParticipantFilter] = useState<ParticipantFilter>('all');
  const [range, setRange] = useState<DashboardRange>(ninetyDaysRange());

  const locationsState = useAsync(() => listLocations(companyId), [companyId]);
  const locations: Location[] = locationsState.data ?? [];

  const target: VitalityTarget = {
    ...(departmentId ? { department_id: departmentId } : { company_id: companyId }),
    ...(locationId !== ALL_LOCATIONS ? { location_id: locationId } : {}),
    ...(participantFilter !== 'all' ? { role: participantFilter } : {}),
  };

  const panels = [...vitalityPanels(target, range), ...trendSeriesPanels(target, range)];
  const batch = useAsync(() => runDashboardBatch(panels), [JSON.stringify(panels)]);
  const spec = (id: string) => panels.find((p) => p.id === id)!;

  const trend = batch.data?.panels.trend;
  const restingCurrent = batch.data?.panels['resting-current'];
  const restingPrevious = batch.data?.panels['resting-previous'];
  const cards = deriveCards(trend, restingCurrent, restingPrevious);
  const dailySeries = deriveDailySeries(batch.data?.panels.daily);
  const dailyStatus = batch.data?.panels.daily?.status;
  const dailyLoading = batch.loading;

  const participationTarget: ParticipationTarget = departmentId
    ? { type: 'department', id: departmentId }
    : { type: 'company', id: companyId };
  const activeUsersSeries = useAsync(
    () =>
      getParticipationSeries(participationTarget, {
        grain: 'week',
        range: 'last_91_days',
        ...(locationId !== ALL_LOCATIONS ? { locationId } : {}),
        ...(participantFilter !== 'all' ? { role: participantFilter } : {}),
      }),
    [departmentId, companyId, locationId, participantFilter],
  );

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3">
        <h2 className="font-heading text-ink text-lg font-semibold">
          {t('overview.trends.title')}
        </h2>

        <div className="flex flex-wrap items-end gap-3">
          <RangeSelect value={range} onChange={setRange} className="mb-0 w-44" />

          <Field label={t('vitality.filters.location.label')} className="mb-0 w-44">
            <Select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value={ALL_LOCATIONS}>{t('vitality.filters.location.all')}</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('vitality.filters.department.label')} className="mb-0 w-44">
            <Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
              <option value="">{t('overview.trends.filters.allDepartments')}</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('overview.trends.filters.participants.label')} className="mb-0 w-44">
            <Select
              value={participantFilter}
              onChange={(e) => setParticipantFilter(e.target.value as ParticipantFilter)}
            >
              <option value="all">{t('overview.trends.filters.participants.all')}</option>
              <option value="manager">{t('overview.trends.filters.participants.manager')}</option>
              <option value="employee">{t('overview.trends.filters.participants.employee')}</option>
            </Select>
          </Field>
        </div>
      </div>

      {batch.error ? (
        <Alert tone="danger">
          {t('statistics.status.loadFailed', { message: batch.error.message })}
        </Alert>
      ) : (
        <div className="flex flex-col gap-6">
          <DailySeriesTrendCard
            title={t('overview.trends.vitalityScore')}
            info={t('overview.trends.info.vitalityScore')}
            points={dailySeries?.vitalityScore ?? []}
            color={colors.mintDeep}
            status={dailyStatus}
            loading={dailyLoading}
            delta={deltaFrom(cards?.vitalityScore.value, cards?.vitalityScore.previous, 'up')}
          />

          <div className="grid gap-6 sm:grid-cols-2">
            <ActiveUsersPerWeekCard state={activeUsersSeries} />
            <TrendCard title={t('overview.trends.sleep')} info={t('overview.trends.info.sleep')}>
              <PanelOutcome spec={spec('sleep-trend')} result={batch.data?.panels['sleep-trend']} />
            </TrendCard>
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <TrendCard title={t('overview.trends.stress')} info={t('overview.trends.info.stress')}>
              <PanelOutcome
                spec={spec('stress-trend')}
                result={batch.data?.panels['stress-trend']}
              />
            </TrendCard>
            <TrendCard title={t('overview.trends.hrv')} info={t('overview.trends.info.hrv')}>
              <PanelOutcome spec={spec('hrv-trend')} result={batch.data?.panels['hrv-trend']} />
            </TrendCard>
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            {/* `LOWER_IS_BETTER.recovery` is `false` (higher recovery is
             * better, same as vitalityScore) — `'up'`, not `'down'`, is the
             * right `goodDirection` here. */}
            <DailySeriesTrendCard
              title={t('overview.trends.recovery')}
              info={t('overview.trends.info.recovery')}
              points={dailySeries?.recovery ?? []}
              color={colors.info}
              status={dailyStatus}
              loading={dailyLoading}
              delta={deltaFrom(cards?.recovery.value, cards?.recovery.previous, 'up')}
            />
            <TrendCard
              title={t('overview.trends.activity')}
              info={t('overview.trends.info.activity')}
            >
              <PanelOutcome
                spec={spec('activity-trend')}
                result={batch.data?.panels['activity-trend']}
              />
            </TrendCard>
          </div>
        </div>
      )}
    </div>
  );
}

/** The three real, API-wired rate metrics on this card — used to build the
 * "which of these currently has no number" list below. Order matches the
 * tiles' left-to-right order. */
const ENERGY_RATE_METRICS = ['breakAdoption', 'focusRhythm', 'coachAdoption'] as const;

/**
 * "Energie & werkbeleving" (Energie-index, Pauze-adoptie, Focusritme,
 * Coach-adoptie, eNPS): § Openstaande punten — two different kinds of gap,
 * not one:
 * - Coach-/Pauze-/Focus-adoptie are now wired to real data:
 *   `rates.ai_coach_use_pct`/`break_adoption_pct`/`focus_rhythm_pct` from
 *   `/statistics/participation`, the last two shipped per
 *   `PLAN-break-focus-adoption-signals.md` in the API repo (mirrors how
 *   `ai_coach_use_pct` already worked). `null` still renders as `—` — a
 *   real "not enough data yet" from the API (the same min-group-size
 *   privacy floor Deelname uses), not a placeholder — the alert below names
 *   exactly which of the three that is, built from `rates` itself rather
 *   than a separate call.
 * - Energie-index and eNPS have *no* candidate source at all, wearable or
 *   behavioral — eNPS in particular is a survey question, never derivable
 *   from stats. These stay marked "Nader te bepalen" so it reads as a
 *   product decision still to make, not a pending API call — the alert
 *   below always names both, regardless of what else is missing, since
 *   that's a different reason ("no source decided") than a rate metric
 *   sitting below the participant floor.
 */
function EnergyWellbeingCard({ rates }: { rates: ParticipationRates | undefined }) {
  const { t } = useTranslation();
  const pct = (value: number | null | undefined) => (value != null ? `${Math.round(value)}%` : '—');

  const rateValues: Record<(typeof ENERGY_RATE_METRICS)[number], number | null | undefined> = {
    breakAdoption: rates?.break_adoption_pct,
    focusRhythm: rates?.focus_rhythm_pct,
    coachAdoption: rates?.ai_coach_use_pct,
  };
  const missingRateLabels = ENERGY_RATE_METRICS.filter((key) => rateValues[key] == null).map(
    (key) => t(`overview.energy.metrics.${key}`),
  );

  return (
    <Card>
      <CardHeader
        title={t('overview.energy.title')}
        icon={Zap}
        action={<Badge tone="outline">{t('overview.energy.badge')}</Badge>}
      />
      <p className="text-accent mb-4 text-sm">{t('overview.energy.subtitle')}</p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatTile
          label={t('overview.energy.metrics.energyIndex')}
          value={
            <Badge tone="outline" wrap>
              {t('overview.energy.tbd')}
            </Badge>
          }
        />
        <StatTile
          label={t('overview.energy.metrics.breakAdoption')}
          value={pct(rates?.break_adoption_pct)}
        />
        <StatTile
          label={t('overview.energy.metrics.focusRhythm')}
          value={pct(rates?.focus_rhythm_pct)}
        />
        <StatTile
          label={t('overview.energy.metrics.coachAdoption')}
          value={pct(rates?.ai_coach_use_pct)}
        />
        <StatTile
          label={t('overview.energy.metrics.enps')}
          value={
            <Badge tone="outline" wrap>
              {t('overview.energy.tbd')}
            </Badge>
          }
        />
      </div>
      <Alert tone="info" className="mt-4">
        <p>{t('overview.energy.missingData.noSource')}</p>
        {missingRateLabels.length > 0 && (
          <p className="mt-1">
            {t('overview.energy.missingData.insufficientParticipants', {
              metrics: missingRateLabels.join(', '),
            })}
          </p>
        )}
      </Alert>
      <p className="text-mint-deep mt-3 flex items-center gap-1.5 text-xs">
        <CheckCircle2 className="size-3.5 shrink-0" aria-hidden="true" />
        {t('overview.energy.privacyNote')}
      </p>
    </Card>
  );
}

/** Statuses that count as "ongoing work" for `PilotStatusCard`'s featured
 * pilot — not yet started (`draft`) and no longer active work
 * (`completed`/`scaled`) are excluded; when nothing qualifies (e.g. every
 * pilot still sits in `draft`), the card falls back to the first pilot
 * anyway so it still shows something rather than nothing. */
const FEATURED_PILOT_STATUSES: PilotStatus[] = [
  'baseline',
  'active',
  'interim_review',
  'final_review',
];

/** Picks the single pilot `PilotStatusCard` features as its hero summary —
 * the first pilot with ongoing work, or just the first pilot when none
 * qualifies. `null` only when `pilots` itself is empty. */
function selectFeaturedPilot(pilots: readonly Pilot[]): Pilot | null {
  return (
    pilots.find((pilot) => FEATURED_PILOT_STATUSES.includes(pilot.status)) ?? pilots[0] ?? null
  );
}

/** Inclusive whole-day count between two dates (`daysBetween(d, d) === 0`). */
function daysBetween(start: string | Date, end: string | Date): number {
  const startDate = start instanceof Date ? start : new Date(start);
  const endDate = end instanceof Date ? end : new Date(end);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((endDate.getTime() - startDate.getTime()) / msPerDay);
}

/** "Dag N van M" for a pilot with both a start and end date — `null` when
 * either is missing, so the card never fabricates a timeline from a partial
 * one. `day` is clamped into `[1, total]` so a pilot that hasn't started yet
 * or has run past its end date still renders a sane position on the bar. */
function pilotDayProgress(pilot: Pilot): { day: number; total: number } | null {
  if (!pilot.start_date || !pilot.end_date) return null;
  const total = daysBetween(pilot.start_date, pilot.end_date) + 1;
  const day = Math.min(total, Math.max(1, daysBetween(pilot.start_date, new Date()) + 1));
  return { day, total };
}

/** The featured pilot's own "hero" summary inside `PilotStatusCard`: name +
 * status badge, "N deelnemers · dag N van M" with a progress bar (or just
 * the participant count when either date is missing), and a link to the
 * pilot's own report. Its own `useAsync` call, keyed on the pilot's id, so
 * switching the featured pilot (a reload with different data) refetches. */
function FeaturedPilotSummary({ pilot }: { pilot: Pilot }) {
  const { t } = useTranslation();
  const participantCount = useAsync(
    () => fetchPilotParticipantCount(pilot.department_ids),
    [pilot.id],
  );
  const count = participantCount.data;
  const progress = pilotDayProgress(pilot);
  const progressPct =
    progress !== null ? Math.min(100, Math.round((progress.day / progress.total) * 100)) : null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-ink text-sm font-semibold">{pilot.name}</h3>
        <Badge tone={statusTone(pilot.status)}>{t(`pilots.status.${pilot.status}`)}</Badge>
      </div>
      {count !== null && (
        <p className="text-accent text-sm">
          {progress !== null
            ? t('overview.pilots.participantsAndDay', {
                count,
                day: progress.day,
                total: progress.total,
              })
            : t('pilots.detail.participantCount', { count })}
        </p>
      )}
      {progressPct !== null && (
        <div className="bg-paper border-line h-2 w-full overflow-hidden rounded-full border">
          <div className="bg-mint h-full" style={{ width: `${progressPct}%` }} />
        </div>
      )}
      <Link
        to={`/pilots/${pilot.id}`}
        className="text-mint-deep mt-1 text-sm font-semibold hover:underline"
      >
        {t('overview.pilots.viewReport')} →
      </Link>
    </div>
  );
}

function PilotStatusCard({
  pilots,
  loading,
  error,
}: {
  pilots: Pilot[] | null | undefined;
  loading: boolean;
  error: Error | null;
}) {
  const { t } = useTranslation();
  const featuredPilot = selectFeaturedPilot(pilots ?? []);
  const otherPilots = featuredPilot
    ? (pilots ?? []).filter((pilot) => pilot.id !== featuredPilot.id)
    : [];

  return (
    <Card>
      <CardHeader title={t('overview.pilots.title')} icon={Rocket} />
      {loading ? (
        <Skeleton kind="lines" />
      ) : error ? (
        <Alert tone="danger">{t('statistics.status.loadFailed', { message: error.message })}</Alert>
      ) : !pilots || pilots.length === 0 || !featuredPilot ? (
        <EmptyState text={t('overview.pilots.empty')} />
      ) : (
        <div className="flex flex-col gap-4">
          <FeaturedPilotSummary pilot={featuredPilot} />
          {otherPilots.length > 0 && (
            <ul className="border-line flex flex-col gap-2 border-t pt-3">
              {otherPilots.map((pilot) => (
                <li key={pilot.id} className="flex items-center justify-between gap-2">
                  <span className="text-ink text-sm">{pilot.name}</span>
                  <Badge tone={statusTone(pilot.status)}>
                    {t(`pilots.status.${pilot.status}`)}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

function ActiveChallengesCard({
  challenges,
  counts,
  loading,
  error,
}: {
  challenges: Challenge[] | undefined;
  counts: ChallengeCounts | undefined;
  loading: boolean;
  error: Error | null;
}) {
  const { t } = useTranslation();
  const activeChallenges = (challenges ?? []).filter((challenge) => challenge.status === 'active');

  return (
    <Card>
      <CardHeader
        title={t('overview.challenges.title')}
        icon={Trophy}
        action={
          counts && (
            <Badge tone="outline">
              {t('overview.challenges.activeBadge', { count: counts.active })}
            </Badge>
          )
        }
      />
      {loading ? (
        <Skeleton kind="lines" />
      ) : error ? (
        <Alert tone="danger">{t('statistics.status.loadFailed', { message: error.message })}</Alert>
      ) : !challenges || challenges.length === 0 ? (
        <EmptyState text={t('overview.challenges.empty')} />
      ) : activeChallenges.length === 0 ? (
        <EmptyState text={t('overview.challenges.noneActive')} />
      ) : (
        <div className="flex flex-col gap-3">
          <ul className="flex flex-col gap-2">
            {activeChallenges.map((challenge) => (
              <li key={challenge.id} className="flex items-center justify-between gap-2">
                <span className="text-ink text-sm">{challenge.title}</span>
                <span className="text-accent text-sm">
                  {t('overview.challenges.participantsCount', {
                    count: challenge.participant_count ?? 0,
                  })}
                </span>
              </li>
            ))}
          </ul>
          <Link to="/challenges" className="text-mint-deep text-sm font-semibold hover:underline">
            {t('overview.challenges.manageLink')} →
          </Link>
        </div>
      )}
    </Card>
  );
}
